/**
 * PriceBookOverlapBanner.tsx — which price book won, when two disagreed.
 *
 * WHY THIS EXISTS
 * ---------------
 * Omar ratified on 25 Aug that a project may hold MORE THAN ONE price list —
 * *"a CME could have used two price list"* — and, in the same breath, that on
 * overlap the newest edition wins **and every overlap is reported**. This is
 * the reporting half.
 *
 * The measurement that produced the decision: two editions of one region
 * sharing tariffa `01.A01.A05.010`, the 2023 book at € 5,64 and the 2025 at
 * € 9,99, priced at **€ 5,64** — the older book — silently, because the
 * staging order was ascending and the matcher takes the first match. Nobody
 * had chosen oldest-wins; it fell out of a stable sort.
 *
 * A framing that was wrong the first time, corrected here rather than dropped:
 * the run's `All matching rows have consistent prices!` is NOT a check that
 * failed on this. It compares the CME against the WINNING book, never the two
 * books against each other. There was no check at all — and the only trace was
 * a per-row `source_file` column in a CSV nobody opens.
 *
 * THE DESIGN RULE, INHERITED FROM ITS TWO SIBLINGS: ABSENCE MUST NEVER READ AS
 * A PASS, and a report that reaches `Job.result` and no screen protects nobody.
 * That is written in `CategoryReconciliationBanner`'s own docstring about
 * itself, and this file exists so it does not have to be written a third time.
 *
 * `applicable: false` — one price book, which is every project until someone
 * uses the library picker — renders NOTHING. It is not an outcome a reader
 * needs; there was no second book to disagree. `null` renders nothing either,
 * but for a different reason, and the two are kept distinct in the accessor
 * rather than collapsed here.
 */
import { type AnalysisJobStatus, type PriceBookOverlap } from "../../lib/tenderApi";

type Tone = "ok" | "warn";

const TONE_STYLE: Record<Tone, { fg: string; bg: string; border: string }> = {
  ok: { fg: "var(--c-ok)", bg: "var(--c-ok-soft)", border: "rgba(47,125,78,0.30)" },
  warn: { fg: "var(--c-warn)", bg: "var(--c-warn-soft)", border: "rgba(184,115,12,0.32)" },
};

/** «€ 5,64» — the symbol FIRST, as `fmtEurCents` renders it in both sibling
 * banners stacked directly above this one on the same page. This file printed
 * «5,64 €», so the Audit page carried two money formats within 200px. */
function fmtEur(v: number): string {
  return `€ ${v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The staged filename, made readable.
 *
 * `used_from` is `os.path.basename` of a staged path, so it carries the
 * `lib{n}_` / `{n}_` prefix `extract.py` invents. That prefix is an
 * implementation detail that appears nowhere else in the product, and this
 * column addresses an Italian professional who sees edition TITLES everywhere
 * else. The prefix does carry one fact worth keeping — whether the book was
 * uploaded for this tender or came from the library — so it is translated
 * rather than dropped. */
function bookLabel(name: string): string {
  const lib = /^lib\d+_(.*)$/.exec(name);
  if (lib) return `${lib[1]} (libreria)`;
  const up = /^\d+_(.*)$/.exec(name);
  if (up) return `${up[1]} (caricato da te)`;
  return name;
}

export interface OverlapCopy {
  tone: Tone;
  title: string;
  detail: string;
}

/** The words and the colour, as a pure function so both are testable.
 *
 * Split out for the reason the money lesson records: colour, icon and ordering
 * are claims as much as the copy is, and 524 tests once passed a change that
 * was honest in its prose and dishonest in its tone token. */
export function priceBookOverlapCopy(
  overlap: PriceBookOverlap | null,
  jobStatus?: AnalysisJobStatus | null,
): OverlapCopy | null {
  if (!overlap) {
    /* A NULL VERDICT HAS THREE CAUSES and only one of them is "this run
       predates the key". Both sibling banners take `jobStatus` for exactly
       this, and `ReconciliationBanner` records why: the moment a user does what
       a red banner told them to do and starts a re-run, the alarm turns silent
       — while the line items on screen still come from the previous run.

       This banner shipped without it, so where an amber «12 voci presenti in
       più listini» stood a second ago there was NOTHING, beside two siblings
       correctly saying the figures are stale. Permanent if the new run dies.
       The one cause the accessor's own comment omitted was the commonest: a
       run in progress. */
    if (jobStatus === "queued" || jobStatus === "running") {
      return {
        tone: "warn",
        title: "Analisi in corso — listini non riverificati",
        detail:
          "Il confronto fra i listini mostrato prima proveniva dall'analisi precedente e non è " +
          "stato riverificato.",
      };
    }
    if (jobStatus === "failed") {
      return {
        tone: "warn",
        title: "Ultima analisi non riuscita — listini non riverificati",
        detail: "Il confronto fra i listini si riferisce a un'analisi precedente.",
      };
    }
    return null;
  }

  /* A STAGED WORKBOOK THAT COULD NOT BE READ outranks everything below, and it
     is reported even when `applicable` is false — which is exactly the case it
     was invisible in.

     `books` is derived from the rows that loaded, so a workbook the pipeline
     gave up on is simply absent from it. Two staged, one corrupt, and the run
     SUCCEEDS: `books` has one entry, `applicable` is false, and this banner
     used to render nothing — while the intake card listed both under «Usati
     per il confronto prezzi». The customer was priced against one book and
     told nothing.

     That is the precise shape this file's own docstring forbids: ABSENCE MUST
     NEVER READ AS A PASS. */
  const unreadable = overlap.unreadable ?? [];
  if (unreadable.length > 0) {
    return {
      tone: "warn",
      title:
        unreadable.length === 1
          ? "Un listino non è stato letto e non ha influito sui prezzi"
          : `${unreadable.length} listini non sono stati letti e non hanno influito sui prezzi`,
      detail:
        `L'analisi non è riuscita ad aprire ${unreadable.map(bookLabel).join(", ")}. I prezzi vengono ` +
        `${overlap.books.length > 0 ? "dagli altri listini" : "da nessun listino"}: ` +
        `controlla il file e rifai l'analisi se il confronto prezzi ti serve completo.`,
    };
  }

  if (!overlap.applicable) return null;

  const n = overlap.books.length;
  if (overlap.codes === 0) {
    // Two books, nothing to arbitrate. Said out loud rather than left silent:
    // a user who deliberately added a second listino is entitled to know it
    // did not quietly overwrite the first.
    return {
      tone: "ok",
      title: "Nessuna sovrapposizione fra i listini",
      detail:
        `L'analisi ha usato ${n} listini prezzi. Nessuna voce compare in più di un listino ` +
        `con prezzi diversi, quindi nessun prezzo è stato scartato.`,
    };
  }
  /* PRECEDENCE HAS TWO RULES, NOT ONE, and this sentence used to state only the
     second — «abbiamo applicato il prezzo del listino più recente».

     Measured by the adversarial review against the real Piemonte books: an
     uploaded `LisPiemonte_OOPP_2023.xlsx` beat the library's 2025 edition on
     **40.355** codes, under a sentence telling the reader the newest had won.
     A listino the user supplied for THIS tender outranks every library edition
     regardless of year — deliberately, and asserted in `extract.py` — because
     digits sort before letters (`0_` < `lib0_`). Only WITHIN the library does
     the newest edition win.

     `priceBookOverlapCopy` receives `books` and `codes` and nothing else, so it
     cannot know which edition is newer: it could not have been right by
     accident. It now states the rule actually in force, and the sentence ends
     by pointing at the table, which is honest per row.

     The copy was corrected rather than the precedence: the precedence is
     ratified and the copy was not. */
  const anyUploaded = overlap.books.some((b) => !b.startsWith("lib"));
  const which = anyUploaded
    ? "un listino che hai caricato tu ha la precedenza su quelli della libreria"
    : "fra i listini della libreria vince l'edizione più recente";
  return {
    tone: "warn",
    title: `${overlap.codes} ${overlap.codes === 1 ? "voce presente" : "voci presenti"} in più listini con prezzi diversi`,
    detail:
      `L'analisi ha usato ${n} listini prezzi. Per queste voci ${which}; gli altri prezzi ` +
      `non sono stati usati. Qui sotto trovi quale listino ha deciso ogni prezzo: controlla ` +
      `che sia quello giusto per questa gara.`,
  };
}

interface Props {
  overlap: PriceBookOverlap | null;
  /** False while the analysis has produced no line items — an un-analysed
   * project shows nothing, exactly as its two siblings do. */
  hasAnalysis: boolean;
  /** Status of the job the overlap was read from — see `priceBookOverlapCopy`. */
  jobStatus?: AnalysisJobStatus | null;
}

export function PriceBookOverlapBanner({
  overlap,
  hasAnalysis,
  jobStatus,
}: Props): JSX.Element | null {
  if (!hasAnalysis) return null;
  const copy = priceBookOverlapCopy(overlap, jobStatus);
  if (!copy) return null;
  const tone = TONE_STYLE[copy.tone];
  const examples = overlap?.examples ?? [];

  return (
    <div
      role="status"
      data-testid="price-book-overlap-banner"
      data-tone={copy.tone}
      style={{
        padding: "11px 14px",
        marginBottom: 16,
        borderRadius: 8,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: tone.fg }}>{copy.title}</div>
      <div
        style={{
          fontSize: 12.5,
          marginTop: 3,
          color: "var(--c-ink-2)",
          lineHeight: 1.5,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {copy.detail}
      </div>

      {examples.length > 0 && (
        /* The offending codes, with BOTH prices and which book each came from.
           Same reasoning as the per-category table beside it: a banner that can
           only say "some prices were overridden" sends the reader to a CSV. The
           whole value here is saying WHICH, and at what. */
        <div style={{ marginTop: 8, overflowX: "auto" }}>
          <table
            style={{
              borderCollapse: "collapse",
              fontSize: 12,
              fontVariantNumeric: "tabular-nums",
              minWidth: 420,
            }}
          >
            <thead>
              <tr style={{ color: "var(--c-ink-3)", textAlign: "left" }}>
                <th style={{ padding: "2px 12px 2px 0", fontWeight: 500 }}>Voce</th>
                <th style={{ padding: "2px 12px 2px 0", fontWeight: 500 }}>Prezzo usato</th>
                <th style={{ padding: "2px 12px 2px 0", fontWeight: 500 }}>Dal listino</th>
                <th style={{ padding: "2px 0", fontWeight: 500 }}>Prezzo non usato</th>
              </tr>
            </thead>
            <tbody>
              {examples.map((e) => (
                <tr key={e.code} style={{ borderTop: "1px solid var(--c-line)" }}>
                  <td style={{ padding: "3px 12px 3px 0" }}>{e.code}</td>
                  <td style={{ padding: "3px 12px 3px 0", fontWeight: 600 }}>{fmtEur(e.used)}</td>
                  <td style={{ padding: "3px 12px 3px 0", color: "var(--c-ink-3)" }}>
                    {bookLabel(e.used_from)}
                  </td>
                  <td style={{ padding: "3px 0", color: "var(--c-ink-3)" }}>
                    {e.others.map((o) => `${fmtEur(o.price)} (${bookLabel(o.from)})`).join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {overlap !== null && overlap.codes > examples.length && (
            /* The count is never capped; the LIST is. Saying so is the
               difference between "these are the ones" and "these are ten of
               them" — a truncation nobody declares reads as completeness. */
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 6 }}>
              Mostrate {examples.length} voci su {overlap.codes}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
